/**
 * G09: Boundary Import
 * Import parcel boundary as garden schemeOutline
 */

import type { ParcelResult, GeoAnchor, EditorCoordinate } from './types'
import { coordinateService } from './CoordinateService'
import { calculateCentroid } from './wktParser'
import { setGeoAnchor } from './geoAnchorState'
import { setParcel } from './parcelState'

const DOTS_PER_METER = 50

/**
 * Import a parcel as the garden's boundary
 * - Calculates geo-anchor from parcel centroid
 * - Transforms boundary to editor coordinates
 * - Returns the schemeOutline object for GardenViewer
 */
export function importParcelAsBoundary(parcel: ParcelResult): {
  anchor: GeoAnchor
  schemeOutline: {
    x: number
    y: number
    points: Array<{ x: number; y: number }>
  }
} {
  // 1. Calculate centroid in PL-2000 (for accuracy)
  const centroidPl2000 = calculateCentroid(parcel.boundaryPl2000)
  const centroidWgs84 = coordinateService.pl2000ToWgs84(centroidPl2000)

  // 2. Create geo-anchor: centroid = editor origin (0, 0)
  const anchor: GeoAnchor = {
    origin: centroidWgs84,
    editorOrigin: { x: 0, y: 0 },
    dotsPerMeter: DOTS_PER_METER
  }

  // Store the anchor in global state
  setGeoAnchor(anchor)

  // Store parcel metadata (includes PL-2000 boundary for merged parcels that can't be re-fetched)
  setParcel({
    id: parcel.id,
    area: parcel.area,
    region: parcel.region,
    address: parcel.address,
    boundaryPl2000: parcel.boundaryPl2000,
  })

  // 3. Transform boundary to editor coordinates
  const editorPoints: EditorCoordinate[] = parcel.boundary.map(coord =>
    coordinateService.wgs84ToEditor(coord, anchor)
  )

  // 4. Create schemeOutline object
  const schemeOutline = {
    x: 0,  // Offset already applied in transformation
    y: 0,
    points: editorPoints.map(p => ({ x: p.x, y: p.y }))
  }

  return { anchor, schemeOutline }
}

/**
 * G12: Calculate the rotation angle (degrees) needed to align
 * the longest edge of a polygon horizontally.
 * Returns a value in [-90, 90] — the smallest rotation to make the longest edge level.
 */
export function calculateAlignmentAngle(points: Array<{ x: number; y: number }>): number {
  if (points.length < 2) return 0

  let longestLen = 0
  let longestAngle = 0

  for (let i = 0; i < points.length; i++) {
    const a = points[i]
    const b = points[(i + 1) % points.length]
    const dx = b.x - a.x
    const dy = b.y - a.y
    const len = Math.sqrt(dx * dx + dy * dy)
    if (len > longestLen) {
      longestLen = len
      longestAngle = Math.atan2(dy, dx)
    }
  }

  // Convert to degrees and normalize to [-90, 90]
  let degrees = longestAngle * (180 / Math.PI)
  // We want to make the edge horizontal (0° or 180°), so we take modulo 180
  degrees = ((degrees % 180) + 180) % 180 // Normalize to [0, 180)
  if (degrees > 90) degrees -= 180         // Map to [-90, 90)
  return -degrees // Negate: rotating boundary by -degrees aligns the edge horizontally
}

/**
 * G12: Rotate boundary points around a pivot point by angleDeg degrees.
 * Returns new point array.
 */
export function rotateBoundaryPoints(
  points: Array<{ x: number; y: number }>,
  angleDeg: number,
  pivot: { x: number; y: number }
): Array<{ x: number; y: number }> {
  const rad = angleDeg * (Math.PI / 180)
  const cosA = Math.cos(rad)
  const sinA = Math.sin(rad)

  return points.map(p => {
    const dx = p.x - pivot.x
    const dy = p.y - pivot.y
    return {
      x: pivot.x + dx * cosA - dy * sinA,
      y: pivot.y + dx * sinA + dy * cosA,
    }
  })
}

/**
 * Calculate the bounding box of the boundary in editor coordinates
 * Useful for camera positioning
 */
export function getBoundaryBounds(points: Array<{ x: number; y: number }>): {
  minX: number
  maxX: number
  minY: number
  maxY: number
  centerX: number
  centerY: number
  width: number
  height: number
} {
  if (points.length === 0) {
    return {
      minX: 0, maxX: 0, minY: 0, maxY: 0,
      centerX: 0, centerY: 0, width: 0, height: 0
    }
  }

  let minX = points[0].x
  let maxX = points[0].x
  let minY = points[0].y
  let maxY = points[0].y

  for (const p of points) {
    if (p.x < minX) minX = p.x
    if (p.x > maxX) maxX = p.x
    if (p.y < minY) minY = p.y
    if (p.y > maxY) maxY = p.y
  }

  return {
    minX,
    maxX,
    minY,
    maxY,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
    width: maxX - minX,
    height: maxY - minY
  }
}
